import { alpha, Box, type SxProps, type Theme, useTheme } from "@mui/material";
import type { FC, ReactNode } from "react";
import { ALPHA, FOCAL_CARD } from "@/theme/tokens";

type Props = {
	/**
	 * The colour the card is about, already resolved from the theme — the brand's for a neutral fact card, a status
	 * palette's (`theme.palette.error.main`) when the card reports that state. It tints the hairline and the wash only;
	 * the text inside keeps its own inks.
	 */
	ink: string;
	children: ReactNode;
	/** Spacing and layout of the card's content (padding, margins, a flex row). The surface itself is the component's. */
	sx?: SxProps<Theme>;
	dataTestId?: string;
};

/**
 * The one focal card of a sheet step — the figure the step is about ("how many", "2/37 now", the facts an approval
 * rests on) raised off the sheet: a `FOCAL_CARD.RADIUS` radius (a multiple of the theme's shape), a hairline in its ink
 * (`ALPHA.STATUS_BORDER`) and a top-down wash of the same ink (`ALPHA.HINT_TINT`) fading out at
 * `FOCAL_CARD.WASH_FADE_STOP` over the paper.
 *
 * Spend it once per step: it is the step's focal moment, and two of them compete. For an inline note use `InlineHint`;
 * for a callout that explains the surface use `SystemBanner`.
 */
export const FocalCard: FC<Props> = ({ ink, children, sx, dataTestId }) => {
	const theme = useTheme();
	return (
		<Box
			data-testid={dataTestId}
			sx={[
				{
					borderRadius: FOCAL_CARD.RADIUS,
					border: `1px solid ${alpha(ink, ALPHA.STATUS_BORDER)}`,
					background: `linear-gradient(180deg, ${alpha(ink, ALPHA.HINT_TINT)}, transparent ${FOCAL_CARD.WASH_FADE_STOP}), ${theme.palette.background.paper}`,
				},
				...(Array.isArray(sx) ? sx : [sx]),
			]}
		>
			{children}
		</Box>
	);
};
